import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import { PractitionersSkeleton } from '../../components/Skeleton'
import {
  Card,
  ContentMaxWidth,
  Muted,
  PageHeading,
  PageSubheading,
  Pill,
  Row,
  Stack,
} from '../../layout/styledComponents'
import { usePatient } from '../../context/PatientContext'
import * as svc from '../../services/practitionersService'
import { humaniseEventType } from './shared'
import { parseUtcDate } from '../../utils/dates'

const AvatarBase = styled.span`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: ${(p) => p.theme?.color?.primary || '#1F3A5F'};
  color: white;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 16px;
  letter-spacing: 0.05em;
  flex-shrink: 0;
  overflow: hidden;
`

const AvatarImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`

const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 9px;
  border-radius: 999px;
  background: ${(p) => p.theme?.color?.bgMuted || 'rgba(31,58,95,0.08)'};
  color: ${(p) => p.theme?.color?.ink || '#1F2937'};
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
`

const ChipCount = styled.span`
  font-size: 11px;
  color: ${(p) => p.theme?.color?.inkMuted || '#6B7280'};
`

function initials(name) {
  if (!name) return '?'
  const parts = name.split(/\s+/).filter(Boolean)
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[1][0]).toUpperCase()
}

function Avatar({ src, name }) {
  const [broken, setBroken] = useState(false)
  if (src && !broken) {
    return (
      <AvatarBase>
        <AvatarImg src={src} alt="" onError={() => setBroken(true)} />
      </AvatarBase>
    )
  }
  return <AvatarBase>{initials(name)}</AvatarBase>
}

export default function PractitionersIndexPage() {
  const { currentProfile } = usePatient()
  const navigate = useNavigate()
  const [items, setItems] = useState(null)
  const [loading, setLoading] = useState(true)

  const refresh = useCallback(async () => {
    if (!currentProfile) return
    setLoading(true)
    try {
      const list = await svc.list(currentProfile)
      setItems(list)
    } finally {
      setLoading(false)
    }
  }, [currentProfile])

  useEffect(() => {
    setItems(null)
    refresh()
  }, [refresh])

  return (
    <ContentMaxWidth>
      <Stack $gap="20px">
        <div>
          <Pill>Practitioners</Pill>
          <PageHeading style={{ marginTop: 12 }}>My practitioners</PageHeading>
          <PageSubheading>Everyone caring for this profile.</PageSubheading>
        </div>

        {loading && !items ? (
          <PractitionersSkeleton />
        ) : !items?.length ? (
          <div
            style={{
              padding: '48px 24px',
              textAlign: 'center',
              border: '1px dashed rgba(31,41,55,0.18)',
              borderRadius: 20,
            }}
          >
            <p style={{ fontSize: 18, marginTop: 0, marginBottom: 8 }}>No practitioners yet.</p>
            <Muted>After your first session, your practitioners show up here.</Muted>
          </div>
        ) : (
          <Stack $gap="10px">
            {items.map((p) => (
              <Card
                key={p.practitionerId}
                as="button"
                onClick={() => navigate(`/patient-home/practitioners/${p.slug || p.practitionerId}`)}
                style={{ textAlign: 'left', cursor: 'pointer', fontFamily: 'inherit' }}
              >
                <Row $gap="14px" style={{ alignItems: 'flex-start' }}>
                  <Avatar src={p.avatarUrl} name={p.displayName} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 16 }}>{p.displayName}</div>
                    <Muted>
                      {p.specialty || 'Practitioner'}
                      {p.brandName ? ` · ${p.brandName}` : ''}
                    </Muted>
                    {p.serviceHistory?.length > 0 && (
                      <Row $gap="6px" $wrap style={{ marginTop: 8 }}>
                        {p.serviceHistory.slice(0, 4).map((sh) => (
                          <Chip key={sh.eventType}>
                            {humaniseEventType(sh.eventType)}
                            {sh.count > 1 && <ChipCount>×{sh.count}</ChipCount>}
                          </Chip>
                        ))}
                      </Row>
                    )}
                    {p.lastInteractionAt && (
                      <Muted style={{ marginTop: 6, fontSize: 12 }}>
                        Last seen{' '}
                        {parseUtcDate(p.lastInteractionAt).toLocaleDateString([], {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        })}
                      </Muted>
                    )}
                  </div>
                  {p.hasActivePack && <Pill $tone="success">Active pack</Pill>}
                </Row>
              </Card>
            ))}
          </Stack>
        )}
      </Stack>
    </ContentMaxWidth>
  )
}
